// src/components/admin/PhotoGalleryManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Image, Plus, Edit2, Trash2, Save, X, Calendar, MapPin, Eye, EyeOff } from 'lucide-react';

const ARCA_GREEN = '#6B8E23';

const PhotoGalleryManagement = () => {
    const [photos, setPhotos] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingPhoto, setEditingPhoto] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    const [photoForm, setPhotoForm] = useState({
        title: '',
        description: '',
        category: 'Events',
        date: '',
        location: '',
        display_order: 0,
        is_active: true
    });

    const API_BASE = process.env.REACT_APP_API_URL;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    if (!API_BASE || !BACKEND_URL) {
        throw new Error('API URLs not configured');
    }

    // Category options based on PhotoGallery.js
    const categoryOptions = [
        'Events',
        'Training',
        'Leadership',
        'Partnerships',
        'Community',
        'Equipment',
        'Research',
        'Chapters'
    ];

    useEffect(() => {
        fetchPhotos();
    }, []);

    const fetchPhotos = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/photo-gallery`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setPhotos(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching photos:', error);
            toast.error('Failed to load photos');
        } finally {
            if (loading) setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size must be less than 5MB');
                return;
            }

            setSelectedFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSavePhoto = async (e) => {
        e.preventDefault();

        console.log('🔍 Form submission - editingPhoto:', editingPhoto);
        console.log('🔍 Form submission - selectedFile:', selectedFile);
        console.log('🔍 Form values:', photoForm);

        // Validation
        if (!editingPhoto && !selectedFile) {
            toast.error('Please select an image file');
            return;
        }

        if (!photoForm.title || !photoForm.category || !photoForm.date || !photoForm.location) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            setUploading(true);
            const token = localStorage.getItem('adminToken');

            const url = editingPhoto
                ? `${API_BASE}/admin/photo-gallery/${editingPhoto.id}`
                : `${API_BASE}/admin/photo-gallery`;

            let response;

            // For updates without a new image, use JSON
            if (editingPhoto && !selectedFile) {
                console.log('📝 Updating without new image - using JSON');
                const updateData = {
                    title: photoForm.title,
                    description: photoForm.description,
                    category: photoForm.category,
                    date: photoForm.date,
                    location: photoForm.location,
                    display_order: photoForm.display_order,
                    is_active: photoForm.is_active ? 1 : 0
                };

                response = await fetch(url, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(updateData)
                });
            } else {
                // For creates or updates with new image, use FormData
                console.log('📤 Creating new photo or updating with new image - using FormData');
                console.log('🖼️ File to upload:', selectedFile?.name, selectedFile?.size, 'bytes');

                if (!selectedFile) {
                    console.error('❌ ERROR: selectedFile is null or undefined!');
                    toast.error('No image file selected. Please select an image.');
                    setUploading(false);
                    return;
                }

                const formData = new FormData();
                formData.append('title', photoForm.title);
                formData.append('description', photoForm.description);
                formData.append('category', photoForm.category);
                formData.append('date', photoForm.date);
                formData.append('location', photoForm.location);
                formData.append('display_order', photoForm.display_order.toString());
                formData.append('is_active', photoForm.is_active ? '1' : '0');
                formData.append('image', selectedFile);

                console.log('📦 FormData entries:');
                for (let pair of formData.entries()) {
                    console.log(pair[0], ':', pair[1]);
                }

                response = await fetch(url, {
                    method: editingPhoto ? 'PUT' : 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                });
            }

            const data = await response.json();

            if (data.success) {
                setShowModal(false);
                resetForm();
                fetchPhotos();
                toast.success(`Photo ${editingPhoto ? 'updated' : 'added'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving photo:', error);
            toast.error('Failed to save photo');
        } finally {
            setUploading(false);
        }
    };

    const handleEditPhoto = (photo) => {
        setEditingPhoto(photo);
        setPhotoForm({
            title: photo.title || '',
            description: photo.description || '',
            category: photo.category || 'Events',
            date: photo.date ? photo.date.split('T')[0] : '',
            location: photo.location || '',
            display_order: photo.display_order || 0,
            is_active: photo.is_active === 1
        });
        setPreviewUrl(`${API_BASE}${photo.image_path}`);
        setShowModal(true);
    };

    const handleDeletePhoto = (id, photoTitle) => {
        // Create a custom confirmation toast
        const confirmDelete = () => {
            deletePhoto(id);
            toast.dismiss('delete-confirm');
        };

        const cancelDelete = () => {
            toast.dismiss('delete-confirm');
        };

        toast(
            <div>
                <div className="d-flex align-items-center mb-2">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-2"
                        style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: ARCA_GREEN,
                            color: 'white'
                        }}
                    >
                        <Trash2 size={20} />
                    </div>
                    <strong style={{ color: ARCA_GREEN }}>Delete Photo?</strong>
                </div>
                <p className="mb-3 small text-muted">Are you sure you want to delete "{photoTitle}"? This action cannot be undone.</p>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={confirmDelete}
                    >
                        Yes, Delete
                    </button>
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={cancelDelete}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                toastId: 'delete-confirm',
                position: 'top-center',
                autoClose: false,
                closeButton: false,
                hideProgressBar: true,
                closeOnClick: false,
                draggable: false,
                style: {
                    borderLeft: `4px solid ${ARCA_GREEN}`
                }
            }
        );
    };

    const deletePhoto = async (id) => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/photo-gallery/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (data.success) {
                fetchPhotos();
                toast.success('Photo deleted successfully');
            } else {
                toast.error(data.message || 'Failed to delete photo');
            }
        } catch (error) {
            console.error('Error deleting photo:', error);
            toast.error('Failed to delete photo');
        }
    };

    const handleToggleActive = async (photo) => {
        try {
            const token = localStorage.getItem('adminToken');

            // Use JSON for updates without file upload
            const updateData = {
                title: photo.title,
                description: photo.description || '',
                category: photo.category,
                date: photo.date.split('T')[0],
                location: photo.location,
                display_order: photo.display_order,
                is_active: photo.is_active === 1 ? 0 : 1
            };

            const response = await fetch(`${API_BASE}/admin/photo-gallery/${photo.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updateData)
            });

            const data = await response.json();

            if (data.success) {
                fetchPhotos();
                toast.success(`Photo ${photo.is_active === 1 ? 'hidden' : 'activated'}`);
            } else {
                toast.error(data.message || 'Failed to update photo status');
            }
        } catch (error) {
            console.error('Error toggling photo status:', error);
            toast.error('Failed to update photo status');
        }
    };

    const resetForm = () => {
        setPhotoForm({
            title: '',
            description: '',
            category: 'Events',
            date: '',
            location: '',
            display_order: 0,
            is_active: true
        });
        setSelectedFile(null);
        setPreviewUrl('');
        setEditingPhoto(null);
    };

    const closeModal = () => {
        setShowModal(false);
        resetForm();
    };

    return (
        <div className="container-fluid p-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1" style={{ color: ARCA_GREEN }}>
                        <Image className="me-2" size={28} />
                        Photo Gallery Management
                    </h2>
                    <p className="text-muted mb-0">
                        Manage photos displayed on the Photo & Event Gallery page
                    </p>
                </div>
                <button
                    className="btn btn-lg fw-bold"
                    style={{ backgroundColor: ARCA_GREEN, color: 'white' }}
                    onClick={() => setShowModal(true)}
                >
                    <Plus size={20} className="me-2" />
                    Add New Photo
                </button>
            </div>

            {/* Statistics */}
            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm" style={{ borderLeft: `4px solid ${ARCA_GREEN}` }}>
                        <div className="card-body">
                            <h6 className="text-muted mb-1">Total Photos</h6>
                            <h3 className="fw-bold mb-0" style={{ color: ARCA_GREEN }}>
                                {photos.length}
                            </h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm" style={{ borderLeft: `4px solid #4CAF50` }}>
                        <div className="card-body">
                            <h6 className="text-muted mb-1">Active Photos</h6>
                            <h3 className="fw-bold mb-0" style={{ color: '#4CAF50' }}>
                                {photos.filter(p => p.is_active === 1).length}
                            </h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm" style={{ borderLeft: `4px solid #2196F3` }}>
                        <div className="card-body">
                            <h6 className="text-muted mb-1">Categories</h6>
                            <h3 className="fw-bold mb-0" style={{ color: '#2196F3' }}>
                                {new Set(photos.map(p => p.category)).size}
                            </h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm" style={{ borderLeft: `4px solid #FF9800` }}>
                        <div className="card-body">
                            <h6 className="text-muted mb-1">Locations</h6>
                            <h3 className="fw-bold mb-0" style={{ color: '#FF9800' }}>
                                {new Set(photos.map(p => p.location)).size}
                            </h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Photos Grid */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <div className="row g-4">
                    {photos.map(photo => (
                        <div key={photo.id} className="col-lg-4 col-md-6">
                            <div className="card h-100 shadow-sm border-0">
                                <div className="position-relative" style={{ height: '200px', overflow: 'hidden' }}>
                                    <img
                                        src={`${BACKEND_URL}${photo.image_path}`}
                                        alt={photo.title}
                                        className="w-100 h-100 object-fit-cover"
                                    />
                                    <div className="position-absolute top-0 end-0 m-2">
                                        <span
                                            className="badge"
                                            style={{ backgroundColor: ARCA_GREEN }}
                                        >
                                            {photo.category}
                                        </span>
                                    </div>
                                    {photo.is_active !== 1 && (
                                        <div
                                            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                                        >
                                            <span className="badge bg-warning">Hidden</span>
                                        </div>
                                    )}
                                </div>
                                <div className="card-body">
                                    <h5 className="card-title fw-bold mb-2">{photo.title}</h5>
                                    <p className="card-text text-muted small mb-3">
                                        {photo.description}
                                    </p>
                                    <div className="d-flex justify-content-between align-items-center text-muted small mb-3">
                                        <div className="d-flex align-items-center">
                                            <Calendar size={14} className="me-1" />
                                            <span>{new Date(photo.date).toLocaleDateString()}</span>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <MapPin size={14} className="me-1" />
                                            <span>{photo.location}</span>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button
                                            className="btn btn-sm btn-outline-primary flex-fill"
                                            onClick={() => handleEditPhoto(photo)}
                                        >
                                            <Edit2 size={14} className="me-1" />
                                            Edit
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => handleToggleActive(photo)}
                                            title={photo.is_active === 1 ? 'Hide' : 'Show'}
                                        >
                                            {photo.is_active === 1 ? <EyeOff size={14} /> : <Eye size={14} />}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleDeletePhoto(photo.id, photo.title)}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN }}>
                                <h5 className="modal-title text-white fw-bold">
                                    {editingPhoto ? 'Edit Photo' : 'Add New Photo'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={closeModal}
                                ></button>
                            </div>
                            <form onSubmit={handleSavePhoto}>
                                <div className="modal-body">
                                    {/* Image Upload */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">
                                            Photo Image *
                                        </label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/*"
                                            onChange={handleFileSelect}
                                            required={!editingPhoto}
                                        />
                                        <small className="text-muted">
                                            Accepted formats: JPG, PNG, GIF (Max 5MB)
                                        </small>
                                        {previewUrl && (
                                            <div className="mt-3">
                                                <img
                                                    src={previewUrl}
                                                    alt="Preview"
                                                    className="img-thumbnail"
                                                    style={{ maxHeight: '200px', width: 'auto' }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Title */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Title *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={photoForm.title}
                                            onChange={(e) => setPhotoForm({ ...photoForm, title: e.target.value })}
                                            required
                                            placeholder="E.g., ARCA Annual Conference 2024"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={photoForm.description}
                                            onChange={(e) => setPhotoForm({ ...photoForm, description: e.target.value })}
                                            placeholder="Brief description of the photo..."
                                        />
                                    </div>

                                    <div className="row">
                                        {/* Category */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Category *</label>
                                            <select
                                                className="form-select"
                                                value={photoForm.category}
                                                onChange={(e) => setPhotoForm({ ...photoForm, category: e.target.value })}
                                                required
                                            >
                                                {categoryOptions.map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Date */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={photoForm.date}
                                                onChange={(e) => setPhotoForm({ ...photoForm, date: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        {/* Location */}
                                        <div className="col-md-8 mb-3">
                                            <label className="form-label fw-bold">Location *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={photoForm.location}
                                                onChange={(e) => setPhotoForm({ ...photoForm, location: e.target.value })}
                                                required
                                                placeholder="E.g., Accra, Ghana"
                                            />
                                        </div>

                                        {/* Display Order */}
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-bold">Display Order</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={photoForm.display_order}
                                                onChange={(e) => setPhotoForm({ ...photoForm, display_order: parseInt(e.target.value) || 0 })}
                                                min="0"
                                            />
                                        </div>
                                    </div>

                                    {/* Active Status */}
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="isActive"
                                            checked={photoForm.is_active}
                                            onChange={(e) => setPhotoForm({ ...photoForm, is_active: e.target.checked })}
                                        />
                                        <label className="form-check-label" htmlFor="isActive">
                                            Active (visible on public gallery)
                                        </label>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}
                                        disabled={uploading}
                                    >
                                        <X size={16} className="me-1" />
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn fw-bold"
                                        style={{ backgroundColor: ARCA_GREEN, color: 'white' }}
                                        disabled={uploading}
                                    >
                                        {uploading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={16} className="me-1" />
                                                {editingPhoto ? 'Update Photo' : 'Add Photo'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhotoGalleryManagement;
